/**
 * useFeatureFlag & useSubscriptionLimit — Hooks para acessar features e limites da assinatura
 *
 * Consulta o endpoint GET /api/v1/me/subscription que retorna:
 * {
 *   plan: { slug: "free", name: "Free" },
 *   features: { "group_scheduling": false, ... },
 *   limits: { "class_max_attendees": 10, "places": 1, ... }
 * }
 *
 * IMPORTANTE: As chaves dentro de `features` e `limits` são snake_case (vêm
 * direto do banco — `subscription_features.feature_key` e
 * `subscription_plan_limits.limit_key`). O backend NÃO converte essas chaves
 * para camelCase porque ASP.NET Core só aplica PropertyNamingPolicy a nomes de
 * propriedade, não a chaves de Dictionary. Use as chaves snake_case nos calls:
 *   useFeatureFlag('group_scheduling')
 *   useSubscriptionLimit('class_max_attendees')
 *
 * Cache: 60 segundos (staleTime) para refletir mudanças administrativas rapidamente.
 */

import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

const SUBSCRIPTION_QUERY_OPTIONS = {
  queryKey: ['subscription'],
  queryFn: async () => {
    const res = await api.get('/v1/me/subscription');
    return res.data;
  },
  staleTime: 60_000, // 60 segundos — considerado fresco
  gcTime: 5 * 60_000, // 5 minutos cache na memória (renomeado de cacheTime na v5)
  refetchOnWindowFocus: false, // não refetch ao voltar para a aba
};

/**
 * Hook para verificar se uma feature está habilitada para o usuário
 * @param {string} featureKey - chave da feature em snake_case (ex: 'group_scheduling')
 * @returns {boolean} true se a feature está habilitada, false caso contrário
 */
export function useFeatureFlag(featureKey) {
  const { data } = useQuery(SUBSCRIPTION_QUERY_OPTIONS);
  return data?.features?.[featureKey] ?? false;
}

/**
 * Hook para obter o valor de um limite da assinatura
 * @param {string} limitKey - chave do limite em snake_case (ex: 'class_max_attendees')
 * @returns {number|null} valor do limite ou null se não disponível / ilimitado
 */
export function useSubscriptionLimit(limitKey) {
  const { data } = useQuery(SUBSCRIPTION_QUERY_OPTIONS);
  return data?.limits?.[limitKey] ?? null;
}

/**
 * Hook para obter todos os dados de assinatura (plan, features, limits).
 * Útil quando precisa de múltiplas informações de uma vez ou do plano em si.
 * @returns {{ plan, planSlug, planName, features, limits, isLoading, error }}
 */
export function useSubscriptionData() {
  const { data, isLoading, error } = useQuery(SUBSCRIPTION_QUERY_OPTIONS);

  return {
    plan: data?.plan || null,
    planSlug: data?.plan?.slug || null,
    planName: data?.plan?.name || null,
    features: data?.features || {},
    limits: data?.limits || {},
    isLoading,
    error,
  };
}

export default useFeatureFlag;
